// src/api/centralMembersApi.ts
//
// Central-direct workspace membership API. Mirrors BrandTalk's
// `web/src/services/membershipApi.ts` suite-standard pattern: browser-direct
// calls to Central using `buildMembersPath` / `buildMemberPath` /
// `buildInvitesPath` / `buildInvitePath` from @levelup/central-auth with
// Bearer auth. No proxy through the CRM backend — Central is the authoritative
// source for workspace membership.
//
// Response shapes are mapped to CRM's existing `TeamMemberSummary` /
// `PendingInvite` so the Settings → Members tab and the invite/role/remove
// flows can swap data sources without UI changes. Clock-in metadata is a
// CRM-only concept that does not live in Central; those fields are defaulted
// on this API (the CRM-specific Team page continues using the BFF `teamApi`
// which overlays clock-in state from the local time-tracking store).

import { createApi } from '@reduxjs/toolkit/query/react';
import {
  buildMembersPath,
  buildMemberPath,
  buildInvitesPath,
  buildInvitePath,
  type WorkspaceMember,
  type PendingInvite as CentralPendingInvite,
  type CentralRole,
  type InviteRoleOption,
} from '@levelup/central-auth';
import { createCentralBaseQuery } from './centralBaseQuery';

/**
 * Central's invite-options path.
 *
 * The installed `@levelup/central-auth` package does not export a path
 * builder for this endpoint yet, but the shared `InviteRoleOption`
 * interface's JSDoc documents it explicitly as "from Central's
 * invite-options endpoint". We target the conventional REST path that
 * mirrors the rest of the membership namespace
 * (`/api/workspaces/:id/members`, `/invites`, `/invites/:id`, etc.) — not
 * a guessed URL, the documented one.
 *
 * If Central does not serve this path, the query errors and
 * `MembersTabShared` falls back to CRM's locally-defined role catalog so
 * the invite UX is never broken.
 */
const buildInviteOptionsPath = (workspaceId: string) =>
  `/api/workspaces/${encodeURIComponent(workspaceId)}/invite-options`;
import type {
  TeamMemberSummary,
  InviteTeamMemberRequest,
  InviteTeamMemberResponse,
} from '../types/api';

function mapMember(m: WorkspaceMember, workspaceId: string): TeamMemberSummary {
  const avatarUrl =
    (m.avatar && 'imageUrl' in m.avatar ? (m.avatar as { imageUrl?: string }).imageUrl : null) ??
    null;
  return {
    userId: m.userId,
    workspaceId,
    fullName: m.fullName,
    email: m.email,
    avatarUrl,
    role: m.role,
    isOwner: m.role === 'owner',
    // Clock-in fields are CRM-specific extensions; Central does not own them.
    isClockedIn: false,
  };
}

function unwrapList<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  if (body && typeof body === 'object') {
    const rec = body as Record<string, unknown>;
    for (const key of ['data', 'members', 'invites']) {
      const v = rec[key];
      if (Array.isArray(v)) return v as T[];
    }
  }
  return [];
}

export const centralMembersApi = createApi({
  reducerPath: 'centralMembersApi',
  baseQuery: createCentralBaseQuery(),
  tagTypes: ['CentralMember', 'CentralInvite'],
  endpoints: (builder) => ({
    getCentralMembers: builder.query<TeamMemberSummary[], { workspaceId: string }>({
      query: ({ workspaceId }) => ({ url: buildMembersPath(workspaceId), method: 'GET' }),
      transformResponse: (body: unknown, _meta, { workspaceId }) =>
        unwrapList<WorkspaceMember>(body).map((m) => mapMember(m, workspaceId)),
      providesTags: (result, _e, { workspaceId }) =>
        result
          ? [
              ...result.map((m) => ({ type: 'CentralMember' as const, id: m.userId })),
              { type: 'CentralMember' as const, id: workspaceId },
            ]
          : [{ type: 'CentralMember' as const, id: workspaceId }],
    }),

    getCentralInviteOptions: builder.query<InviteRoleOption[], { workspaceId: string }>({
      query: ({ workspaceId }) => ({
        url: buildInviteOptionsPath(workspaceId),
        method: 'GET',
      }),
      transformResponse: (body: unknown): InviteRoleOption[] => {
        const list = unwrapList<Record<string, unknown>>(body);
        return list
          .map((raw): InviteRoleOption | null => {
            const coreRole = raw.coreRole as CentralRole | undefined;
            const id = (raw.id as string | undefined) ?? (raw.key as string | undefined);
            if (!coreRole || !id) return null;
            return {
              id,
              coreRole,
              templateId: (raw.templateId as string | null | undefined) ?? null,
              displayLabel:
                (raw.displayLabel as string | undefined) ??
                (raw.label as string | undefined) ??
                id,
              description: (raw.description as string | null | undefined) ?? null,
              group: (raw.group === 'template' ? 'template' : 'core'),
            };
          })
          .filter((o): o is InviteRoleOption => o !== null);
      },
      providesTags: (_r, _e, { workspaceId }) => [
        { type: 'CentralInvite' as const, id: `${workspaceId}:options` },
      ],
    }),

    getCentralPendingInvites: builder.query<CentralPendingInvite[], { workspaceId: string }>({
      query: ({ workspaceId }) => ({ url: buildInvitesPath(workspaceId), method: 'GET' }),
      // Normalize Central's response into the canonical `PendingInvite` shape
      // declared by `@levelup/central-auth`. Central's actual HTTP payload may
      // name the identifier `id` (or `inviteId`); the shared
      // `PendingInvitesList` reads `invite.inviteId`, so we guarantee it.
      transformResponse: (body: unknown) =>
        unwrapList<Record<string, unknown>>(body).map((raw): CentralPendingInvite => {
          const inviteId =
            (raw.inviteId as string | undefined) ??
            (raw.id as string | undefined) ??
            '';
          return {
            inviteId,
            email: (raw.email as string | undefined) ?? '',
            role: (raw.role as CentralPendingInvite['role']) ?? 'member',
            roleLabel: (raw.roleLabel as string | null | undefined) ?? null,
            invitedBy:
              (raw.invitedBy as CentralPendingInvite['invitedBy'] | undefined) ?? null,
            sentAt: (raw.sentAt as string | undefined) ?? '',
            expiresAt: (raw.expiresAt as string | null | undefined) ?? null,
          };
        }),
      providesTags: (_r, _e, { workspaceId }) => [
        { type: 'CentralInvite' as const, id: workspaceId },
      ],
    }),

    centralInviteMember: builder.mutation<
      InviteTeamMemberResponse,
      InviteTeamMemberRequest & { workspaceId: string }
    >({
      query: ({ workspaceId, email, role, templateId }) => ({
        url: buildMembersPath(workspaceId),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: { email, role: role ?? 'member', templateId },
      }),
      transformResponse: (_body, _meta, { email, role, workspaceId }) => ({
        message: 'Invitation sent',
        email,
        role: role ?? 'member',
        workspaceId,
      }),
      invalidatesTags: (_r, _e, { workspaceId }) => [
        { type: 'CentralInvite' as const, id: workspaceId },
        { type: 'CentralMember' as const, id: workspaceId },
      ],
    }),

    centralUpdateMemberRole: builder.mutation<
      { userId: string; workspaceId: string; role: string; message: string },
      { workspaceId: string; memberId: string; role: CentralRole | 'owner' | 'admin' | 'member' | 'viewer' }
    >({
      query: ({ workspaceId, memberId, role }) => ({
        url: buildMemberPath(workspaceId, memberId),
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: { role },
      }),
      transformResponse: (_body, _meta, { workspaceId, memberId, role }) => ({
        userId: memberId,
        workspaceId,
        role,
        message: 'Role updated',
      }),
      invalidatesTags: (_r, _e, { workspaceId, memberId }) => [
        { type: 'CentralMember' as const, id: memberId },
        { type: 'CentralMember' as const, id: workspaceId },
      ],
    }),

    centralRemoveMember: builder.mutation<
      { success: boolean; message: string },
      { workspaceId: string; memberId: string }
    >({
      query: ({ workspaceId, memberId }) => ({
        url: buildMemberPath(workspaceId, memberId),
        method: 'DELETE',
      }),
      transformResponse: () => ({ success: true, message: 'Member removed' }),
      invalidatesTags: (_r, _e, { workspaceId }) => [
        { type: 'CentralMember' as const, id: workspaceId },
      ],
    }),

    centralCancelInvite: builder.mutation<
      { success: boolean },
      { workspaceId: string; inviteId: string }
    >({
      query: ({ workspaceId, inviteId }) => ({
        url: buildInvitePath(workspaceId, inviteId),
        method: 'DELETE',
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (_r, _e, { workspaceId }) => [
        { type: 'CentralInvite' as const, id: workspaceId },
      ],
    }),
  }),
});

export const {
  useGetCentralMembersQuery,
  useGetCentralInviteOptionsQuery,
  useGetCentralPendingInvitesQuery,
  useCentralInviteMemberMutation,
  useCentralUpdateMemberRoleMutation,
  useCentralRemoveMemberMutation,
  useCentralCancelInviteMutation,
} = centralMembersApi;
